/**
 * Tier 1 → Tier 2 composition mechanism (ADR-0004).
 *
 * Shared code has to invoke vertical behaviour without importing a vertical.
 * The backend needs 18 keyed registry hooks for this because one process
 * serves every vertical and must resolve per request. A Tier 3 frontend app
 * serves exactly one vertical *by deployment*, so composition happens at
 * compile time: the shell imports one Tier 2 package and provides it here.
 *
 * Tier 1 knows the module's *shape* and never its contents. It exposes three
 * things and nothing else:
 *
 *   <VerticalModuleProvider module={educationModule}>   Tier 3 composes
 *   useVerticalModule()                                 Tier 1 reads
 *   <VerticalSlot name="entityDetailPanel" />           Tier 1 renders
 *
 * Apps with no vertical (admin-portal, marketing) provide no module:
 * `useVerticalModule()` returns null and every slot renders its fallback.
 * There is no special case for them.
 */

import {
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react'

import type { ImportColumnDef } from '../hooks/useVerticalConfig'

/**
 * A slot component receives whatever props the Tier 1 call site passes. Tier 1
 * cannot know their shape without naming vertical concepts, which is the whole
 * point of the slot — so this is one deliberate `any`, contained to this line.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type VerticalSlotComponent = ComponentType<any>

export interface VerticalNavItem {
  to: string
  label: string
  icon?: ComponentType<{ className?: string }>
  end?: boolean
  /**
   * Feature key the tenant must have enabled for this item to show. The key is
   * a string the vertical declares; Tier 1 never learns what it means.
   */
  requiresFeature?: string
  /** Permission the operator must hold. Same opacity: Tier 1 only checks it. */
  requiresPermission?: string
  /**
   * Which nav section this belongs to in an operator shell. Tier 1 owns the
   * sections and their order; the vertical says only which one it joins.
   */
  group?: 'workspace' | 'people' | 'finance' | 'engagement' | 'reports' | 'settings'
  /**
   * For `group: 'workspace'` only: the heading of the section this item sits
   * under ("School day", "Properties"). The vertical names its own sections;
   * the shell renders one per distinct value, in declaration order, ahead of
   * the shared sections.
   */
  section?: string
  /** Extra search terms for the command palette. */
  keywords?: string[]
  /**
   * Only for organisations run these ways (e.g. ['agency_managed']). Missing:
   * every way. The vertical declares the model strings; Tier 1 compares them.
   */
  operatingModels?: string[]
}

export interface VerticalRoute {
  path: string
  element: ReactElement
  /** As `VerticalNavItem.requiresFeature`, applied to the route itself. */
  requiresFeature?: string
  /**
   * Permission required to open the route directly. Declared by the vertical
   * because only it knows which of its pages is sensitive; enforced by the
   * shell, which knows nothing about what the permission protects.
   */
  requiresPermission?: string
}

/**
 * What a Tier 2 package declares about itself.
 *
 * `TContext` is the shell's context payload — the payer portals use
 * `PortalContextResponse` — kept generic so Tier 1 never names a vertical's
 * context shape.
 */
export interface VerticalModule<TContext = unknown> {
  /** Vertical code, matching the backend's (`school`, `rental`). */
  code: string
  /** Fallback context while the server's per-tenant context is loading. */
  defaultContext?: TContext
  routes?: VerticalRoute[]
  navItems?: VerticalNavItem[]
  /**
   * Icon for this vertical's billed entity (a student, a unit). Shared nav
   * renders it without knowing which noun it stands for.
   */
  entityIcon?: ComponentType<{ className?: string }>
  slots?: Record<string, VerticalSlotComponent>
  importColumns?: ImportColumnDef[]
  /**
   * Renames for shared nav entries, keyed by href ('/clients' -> 'Tenants').
   * The shell's defaults are neutral; only the vertical knows what its
   * operators call the people on those pages.
   */
  sharedNavLabels?: Record<string, string>
  /**
   * Shared nav entries (by href) this vertical replaces with its own page,
   * e.g. rental hides '/entities' because its unit roster does that job.
   * The route stays reachable; only the menu entry goes.
   */
  hiddenSharedNav?: string[]
  /**
   * Shared pages this vertical does with a page of its own, by href
   * ('/deposits-to-assign' -> '/payments?view=not_matched'). Wherever the
   * shell links to the shared page (Home, the phone tab bar, shortcuts), it
   * links here instead.
   */
  sharedPageFor?: Record<string, string>
  /**
   * The vertical sends its own messages to payers (rental: Settings ›
   * Messages), so the shell's generic payment-reminder settings don't apply.
   */
  ownsPayerMessages?: boolean
  /**
   * Per operating model: menu entries (by href) to hide and to rename, for
   * shared and vertical entries alike. A landlord running their own building
   * has no owners to pay out, so their menu differs from an agency's.
   */
  operatingModelNav?: Record<string, { hidden?: string[]; labels?: Record<string, string> }>
  /**
   * Extra Settings tabs this vertical owns, each rendering one of its slots.
   * The shell lists them after its own "Organisation" tab without knowing
   * what a grade or a late fee is.
   */
  settingsTabs?: { id: string; label: string; slot: string }[]
  /**
   * The nouns staff screens use (entity, payer, group, obligation, period,
   * entity_reference). The vertical owns its vocabulary, so these win over the
   * labels stored with the vertical on the server, which predate them.
   */
  labels?: Record<string, string>
  /**
   * The operator sign-in screen in this vertical's words: the portal's name,
   * the line under the headline, three short points and an example email.
   * Missing: the shell's neutral wording.
   */
  operatorLogin?: {
    portalName: string
    tagline: string
    points: { title: string; description: string }[]
    emailExample: string
  }
  /**
   * This vertical's words for why a charge is waived. The codes are neutral
   * and shared with the backend; a school says "paid in kind", a property
   * manager says "tenant paid for a repair". Missing codes use the shell's
   * defaults.
   */
  waiverReasons?: Partial<Record<WaiverReasonCode, WaiverReasonCopy>>
  /** Names for the built-in roles ('principal' -> 'Manager'). */
  roleLabels?: Record<string, string>
  /**
   * The kinds of one-off charge staff can add to an account, in this
   * vertical's words. Codes are the backend's obligation categories.
   */
  chargeCategories?: { code: string; label: string }[]
  /**
   * What this vertical adds to Home's one "Needs you" list. The shell owns
   * the list (approvals, money to assign, conversations); the vertical names
   * an endpoint returning counted signals and says each one in its own words.
   */
  needsYou?: NeedsYouSource
  /**
   * The vertical's Home (2026-10-16). With it, Home is Needs you, then the
   * vertical's slot `homeMonth` (its period: the figures its Reports use)
   * in place of the shell's all-time Collection block, then these actions
   * in place of "Go to" and "Quick actions" (which repeat the menu).
   */
  home?: { actions: HomeAction[] }
  /**
   * Records search can find (units, people, owners...): a GET endpoint taking
   * `q` and returning RecordSearchResult[]. Search then opens things, not
   * just pages.
   */
  recordSearch?: { path: string }
}

/** One thing search found, with the address of its record. */
export interface RecordSearchResult {
  kind: string
  id: string
  title: string
  subtitle: string | null
  path: string
}

/** A counted thing to do, as a vertical's signals endpoint returns it. */
export interface NeedsYouSignal {
  key: string
  count: number
  amount: string | null
  /** The page and saved view that shows exactly these. */
  path: string
  view: string | null
}

/**
 * Where an item sits in Needs you: decisions first, then money, repairs, and
 * things to put right; "coming_up" needs no action today and shares one line.
 */
export type NeedsYouGroup = 'decide' | 'money' | 'repairs' | 'fix' | 'coming_up'

export interface NeedsYouSource {
  /** GET endpoint returning NeedsYouSignal[] for the signed-in person. */
  path: string
  /**
   * Who may call it; without this permission the vertical adds nothing.
   * Omit it when the endpoint checks each signal's own permission.
   */
  requiresPermission?: string
  /** The signal in words, or null to leave it out. */
  describe: (signal: NeedsYouSignal) => {
    title: string; detail?: string; tone: 'danger' | 'warning' | 'neutral'; group?: NeedsYouGroup
  } | null
}

/** Something to start from Home, opening its screen's form. */
export interface HomeAction {
  label: string
  to: string
  requiresPermission: string
  /** Only for these operating models (rental: an agency pays owners). */
  operatingModels?: string[]
}

export type WaiverReasonCode = 'in_kind' | 'concession' | 'charged_in_error' | 'other'

export interface WaiverReasonCopy {
  label: string
  /** One line under the choice: when to use it. */
  hint?: string
  /** What to attach, in this vertical's terms. */
  evidenceHint?: string
  /** Label for the in-kind details box, when the reason needs one. */
  detailsLabel?: string
  /**
   * A confirmation some charges need before the request can be sent (rental:
   * the landlord agreed to a rent concession in writing).
   */
  consentPrompt?: string
}

const VerticalModuleContext = createContext<VerticalModule<unknown> | null>(null)

export interface VerticalModuleProviderProps<TContext> {
  module: VerticalModule<TContext>
  children: ReactNode
}

/** Provides the one vertical this app was built with. */
export function VerticalModuleProvider<TContext>({
  module,
  children,
}: VerticalModuleProviderProps<TContext>) {
  return (
    <VerticalModuleContext.Provider value={module as VerticalModule<unknown>}>
      {children}
    </VerticalModuleContext.Provider>
  )
}

/** The provided module, or null in an app with no vertical. */
export function useVerticalModule<TContext = unknown>(): VerticalModule<TContext> | null {
  return useContext(VerticalModuleContext) as VerticalModule<TContext> | null
}

export interface VerticalSlotProps {
  /** Slot name the vertical fills in its `slots` map. */
  name: string
  /** Rendered when no module is provided, or the module leaves this slot empty. */
  fallback?: ReactNode
  /** Everything else is forwarded to the slot component. */
  [prop: string]: unknown
}

/**
 * Renders the vertical's component for `name`, or `fallback` when there is
 * none. Slot names are strings, so a typo degrades to the fallback rather than
 * crashing — always pass a fallback you are happy for a user to see.
 */
export function VerticalSlot({ name, fallback = null, ...props }: VerticalSlotProps) {
  const module = useVerticalModule()
  const Component = module?.slots?.[name]
  if (!Component) return <>{fallback}</>
  return createElement(Component, props)
}
